import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Brain, Zap, Lock, Bot, Code, CloudLightning, Network, Database, Key, BarChart3, ChevronRight, CheckCircle } from 'lucide-react';
import './FeaturesPage.css';

// Detailed features data with slugs and full content
export const featuresData = [
  {
    icon: Brain,
    slug: "ai-search-chat",
    title: "AI-Powered Search & Chat",
    description: "Experience the next generation of enterprise search with our advanced AI that understands context and intent.",
    shortItems: [
      "Natural language processing for intuitive searching",
      "Context-aware chat interface with source citations",
      "Cross-document knowledge synthesis",
      "Multi-language support and semantic understanding"
    ],
    detailContent: {
      overview: "Unlock the full potential of your organization's knowledge with Ufficient's advanced AI search and chat capabilities. Our system goes beyond traditional search, understanding context and intent to deliver precise answers.",
      keyPoints: [
        {
          title: "Natural Language Understanding",
          description: "Ask questions in plain English and receive accurate, contextual responses.",
          benefits: [
            "Intuitive query processing",
            "Context retention across conversations",
            "Multi-language support",
            "Semantic search capabilities"
          ]
        },
        {
          title: "Source-Grounded Responses",
          description: "Every answer comes with citations and links to source documents.",
          benefits: [
            "Automatic citation generation",
            "Direct source linking",
            "Quote extraction",
            "Context preservation"
          ]
        },
        {
          title: "Cross-Document Intelligence",
          description: "Connect information across your entire document base.",
          benefits: [
            "Automatic relationship mapping",
            "Knowledge synthesis",
            "Pattern recognition",
            "Insight generation"
          ]
        }
      ]
    }
  },
  {
    icon: Database,
    slug: "document-management",
    title: "Intelligent Document Management",
    description: "Transform how your organization handles documents with our comprehensive management system.",
    shortItems: [
      "Automated document categorization and tagging",
      "Version control and change tracking",
      "Advanced permission management",
      "Seamless integration with existing storage solutions"
    ],
    detailContent: {
      overview: "Modernize your document management with AI-powered organization, intelligent categorization, and seamless integration capabilities.",
      keyPoints: [
        {
          title: "Smart Organization",
          description: "Automatically categorize and tag documents for easy retrieval.",
          benefits: [
            "AI-powered categorization",
            "Automatic metadata generation",
            "Smart tagging system",
            "Hierarchical organization"
          ]
        },
        {
          title: "Version Control",
          description: "Track changes and maintain document history with precision.",
          benefits: [
            "Complete revision history",
            "Change tracking",
            "Version comparison",
            "Rollback capabilities"
          ]
        },
        {
          title: "Access Control",
          description: "Manage permissions and ensure document security.",
          benefits: [
            "Role-based access",
            "Granular permissions",
            "Activity logging",
            "Security compliance"
          ]
        }
      ]
    }
  },
  {
    icon: CloudLightning,
    slug: "bidai-contract-analysis",
    title: "BidAI & Contract Analysis",
    description: "Revolutionize your RFP process and contract management with AI-powered automation.",
    shortItems: [
      "Automated RFP response generation",
      "Requirement extraction and tracking",
      "Contract analysis and risk assessment",
      "Compliance checking and validation"
    ],
    detailContent: {
      overview: "Transform your bidding process and contract management with AI-powered automation that understands complex requirements, generates responses, and ensures compliance.",
      keyPoints: [
        {
          title: "Automated RFP Processing",
          description: "Automatically analyze RFPs and generate winning responses.",
          benefits: [
            "Faster response time",
            "Requirements auto-extraction",
            "Smart template matching",
            "Past proposal learning"
          ]
        },
        {
          title: "Contract Intelligence",
          description: "Deep analysis of contracts for risks and obligations.",
          benefits: [
            "Risk identification",
            "Obligation tracking",
            "Clause comparison",
            "Compliance validation"
          ]
        },
        {
          title: "Requirement Management",
          description: "Track and manage requirements throughout the project lifecycle.",
          benefits: [
            "Automated tracking",
            "Change impact analysis",
            "Progress monitoring",
            "Dependency mapping"
          ]
        }
      ]
    }
  },
  {
    icon: Network,
    slug: "recruitai-talent",
    title: "RecruitAI & Talent Management",
    description: "Transform your recruitment process with intelligent candidate matching and assessment.",
    shortItems: [
      "AI-powered resume parsing and analysis",
      "Smart candidate-job matching",
      "Automated skill assessment",
      "Interview question generation and scoring"
    ],
    detailContent: {
      overview: "Streamline your recruitment process with AI that understands candidate qualifications, matches them to roles, and helps you make better hiring decisions.",
      keyPoints: [
        {
          title: "Intelligent Resume Analysis",
          description: "Automatically extract and understand candidate qualifications.",
          benefits: [
            "Skill extraction",
            "Experience validation",
            "Education verification",
            "Qualification scoring"
          ]
        },
        {
          title: "Smart Matching Engine",
          description: "Match candidates to roles based on multiple criteria.",
          benefits: [
            "Skill alignment",
            "Experience matching",
            "Culture fit assessment",
            "Growth potential analysis"
          ]
        },
        {
          title: "Interview Assistance",
          description: "Generate relevant questions and evaluate responses.",
          benefits: [
            "Custom question generation",
            "Response evaluation",
            "Feedback automation",
            "Interview scoring"
          ]
        }
      ]
    }
  },
  {
    icon: Lock,
    slug: "enterprise-security",
    title: "Enterprise-Grade Security",
    description: "Rest easy knowing your sensitive data is protected by military-grade security measures.",
    shortItems: [
      "End-to-end AES-256 encryption",
      "Role-based access control (RBAC)",
      "Comprehensive audit logging",
      "Compliance with major security standards"
    ],
    detailContent: {
      overview: "Protect your organization's knowledge with enterprise-grade security that ensures data privacy, controls access, and maintains compliance.",
      keyPoints: [
        {
          title: "Advanced Encryption",
          description: "Protect data with military-grade encryption standards.",
          benefits: [
            "AES-256 encryption",
            "End-to-end protection",
            "Key management",
            "Secure transmission"
          ]
        },
        {
          title: "Access Control",
          description: "Granular control over who can access what information.",
          benefits: [
            "Role-based access",
            "Permission management",
            "User authentication",
            "Session control"
          ]
        },
        {
          title: "Compliance & Auditing",
          description: "Meet security standards and track all system activity.",
          benefits: [
            "Activity logging",
            "Compliance reporting",
            "Security alerts",
            "Audit trails"
          ]
        }
      ]
    }
  },
  {
    icon: Bot,
    slug: "ai-assistant",
    title: "Intelligent AI Assistant",
    description: "Your team's AI companion for navigating and utilizing your organization's knowledge base.",
    shortItems: [
      "Context-aware conversations",
      "Document-grounded responses",
      "Multi-turn dialogue support",
      "Customizable AI behavior"
    ],
    detailContent: {
      overview: "Experience a new level of productivity with an AI assistant that understands your organization's context and helps teams work smarter.",
      keyPoints: [
        {
          title: "Contextual Understanding",
          description: "Maintain context across complex conversations.",
          benefits: [
            "Thread memory",
            "Context retention",
            "Intent recognition",
            "Personalized responses"
          ]
        },
        {
          title: "Knowledge Integration",
          description: "Access and utilize your organization's knowledge base.",
          benefits: [
            "Document grounding",
            "Cross-reference capability",
            "Source citation",
            "Knowledge synthesis"
          ]
        },
        {
          title: "Workflow Automation",
          description: "Automate routine tasks and processes.",
          benefits: [
            "Task automation",
            "Process optimization",
            "Workflow integration",
            "Custom actions"
          ]
        }
      ]
    }
  }
];

// Feature card component for grid layout
const FeatureCard = ({ feature, index }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate(`/features/${feature.slug}`);
  };
  
  return (
    <motion.div
      className="feature-card"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      onClick={handleClick}
    >
      <div className="feature-card-icon">
        <feature.icon className="w-8 h-8" />
      </div>
      <h3 className="feature-card-title">{feature.title}</h3>
      <p className="feature-card-description">{feature.description}</p>
      <div className="feature-card-arrow">
        <ChevronRight className="w-5 h-5" />
      </div>
    </motion.div>
  );
};

// Individual feature section for detailed view
const FeatureSection = ({ feature, index }) => {
  const navigate = useNavigate();
  
  return (
    <motion.section
      className="feature-detail-section"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="feature-detail-container">
        {/* Hero Area */}
        <motion.div 
          className="feature-hero-area"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="feature-hero-icon">
            <feature.icon className="w-16 h-16" />
          </div>
          <h1 className="feature-hero-title">{feature.title}</h1>
          <p className="feature-hero-description">{feature.description}</p>
          <button 
            className="feature-hero-button"
            onClick={() => navigate(`/features/${feature.slug}`)}
          >
            Schedule Consultation
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </motion.div>

        {/* Feature Benefits */}
        <motion.div 
          className="feature-benefits"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="feature-benefits-title">Key Benefits</h2>
          <div className="feature-benefits-grid">
            {feature.shortItems.map((item, idx) => (
              <motion.div
                key={idx}
                className="feature-benefit-item"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + idx * 0.1 }}
              >
                <CheckCircle className="feature-benefit-icon" />
                <span>{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Main hero section
const HeroSection = () => (
  <motion.div
    className="features-hero"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.8 }}
  >
    <motion.h1
      className="features-hero-title"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.8 }}
    >
      Enterprise Features for
      <br />
      <span className="gradient-text">Modern Teams</span>
    </motion.h1>
    <motion.p
      className="features-hero-description"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.8 }}
    >
      Discover how Ufficient's powerful features can transform your organization's knowledge management
      and accelerate your team's productivity.
    </motion.p>
  </motion.div>
);

// CTA Section
const CTASection = () => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      className="features-cta"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h2 className="features-cta-title">
        Ready to Transform Your Organization?
      </h2>
      <p className="features-cta-description">
        Join the leading companies using Ufficient to revolutionize their knowledge management.
      </p>
      <motion.button
        className="features-cta-button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate('/contact')}
      >
        Get Started Today
        <ChevronRight className="cta-arrow" />
      </motion.button>
    </motion.div>
  );
};

const FeaturesPage = () => {
  return (
    <div className="features-page">
      <HeroSection />
      
      {/* Features Grid */}
      <motion.div 
        className="features-grid-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="features-grid-container">
          <div className="features-grid">
            {featuresData.map((feature, index) => (
              <FeatureCard key={feature.slug} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </motion.div>
      
      <CTASection />
    </div>
  );
};

export default FeaturesPage;